import React, { useMemo, useState, useEffect } from 'react';
import styles from '../../../PackageBuilders/MultiPose/MultiPose.module.scss';
import Carousel from '../../../PackageBuilders/MultiPose/Carousel';
import { useIndex } from '../../../PackageBuilders/MultiPose/hooks';
import PreviewArea from '../../../PackageBuilders/MultiPose/PreviewArea';
import cn from 'classnames';
import ReviewModal from '../../../PackageBuilders/MultiPose/ReviewModal';
import eligiblePoses from '../../../PackageBuilders/MultiPose/poseEligibility';
import useImageOrientations from '../../../../hooks/useImageOrientations';

// ⚠️ THIS IS THE THIRD POSE PICKER, and it was the one in the screenshots. It
// borrows MultiPose's Carousel and PreviewArea but never passes through
// MultiPose/Builder, so the placement rule wired there (#5733, #5734) changed
// nothing here. Anything that imports MultiPose/Carousel is a pose picker and
// has to ask poseEligibility itself.
const NO_USABLE_POSE = 'This design needs an upright cut-out photo, and none of the photos in this session fit it.';
const PICK_ANOTHER_POSE = 'This design needs an upright cut-out photo — choose one of the photos below.';

export default function Builder({
                                  backgrounds,
                                  poses,
                                  initialSheets,
                                  isLoading,
                                  option,
                                  packageId,
                                  errors,
                                  onConfirm,
                                  reviewSheets = [],
                                  offer = null,
                                  qualifiesForFreeAnnouncements = false,
                                  orderPackage = null,
                                }) {
  const [sheets, setSheets] = useState(initialSheets);

  const {
    decrement,
    decrementDisabled,
    increment,
    incrementDisabled,
    index: currentSheetIndex,
    setIndex,
  } = useIndex({ maxIndex: sheets.length - 1 });

  const availableProductTypes = useMemo(() => {
    if (!initialSheets[currentSheetIndex]) return '';

    return initialSheets[currentSheetIndex].addon.cssName;
  }, [currentSheetIndex, initialSheets[currentSheetIndex], sheets[currentSheetIndex]?.imageUrl]);

  // THE ADD-ON THIS SHEET IS FOR. addonSheetToSheet keeps it on the sheet, and
  // this file already reads `.addon.cssName` / `.addon.backgroundSelection`
  // off it -- the same object, so the rule reaches here through the same path
  // the existing code trusts.
  const currentAddon = sheets[currentSheetIndex]?.addon || initialSheets[currentSheetIndex]?.addon;

  // Orientation is MEASURED, never read off the filename; one probe per image
  // into the shared cache. Unmeasured poses are kept until they are known.
  const posePreviewUrls = useMemo(
    () => (poses || []).map((p) => p.watermarkUrl || p.imageUrl).filter(Boolean),
    [poses],
  );
  const poseOrientations = useImageOrientations(posePreviewUrls);

  // WHICH PHOTOS THIS PRODUCT CAN HOLD -- the same rule the gifts product
  // screen and the MultiPose builder ask. (The old memo depended only on the
  // sheet index, so it also never saw poses that arrived after first render.)
  const posesToDisplay = useMemo(() => {
    const forProduct = (poses || []).filter((pose) => {
      if (availableProductTypes === 'sibling') return pose.type === 'SiblingImage';

      return true;
    });

    return eligiblePoses(forProduct, currentAddon, poseOrientations);
  }, [currentSheetIndex, poses, currentAddon, poseOrientations, availableProductTypes]);

  // ⚠️ THE SHEET'S OWN POSE IS JUDGED TOO. This sheet already holds whatever
  // was chosen before -- possibly before the rule existed. Filtering what can
  // be CHOSEN while confirming what is already chosen would send the refused
  // pose to print anyway. The customer is told to pick another, and confirm
  // stays disabled until they do; nothing is swapped silently.
  const sheetPose = sheets[currentSheetIndex]?.pose;
  const sheetPoseAllowed = !currentAddon || !sheetPose
    || eligiblePoses([sheetPose], currentAddon, poseOrientations).length > 0;
  const nothingToChoose = !!currentAddon && posesToDisplay.length === 0;

  const backgroundsToDisplay = useMemo(() => (
    // backgrounds.filter((bg) => bg.productTypeNames.includes(availableProductTypes))
    backgrounds
  ), [availableProductTypes]);

  const setBackground = (newBackground) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    if (!currentSheet) return;

    currentSheet.background = newBackground;
    currentSheet.backgroundId = newBackground.id;

    setSheets((prev) => ([...prev]));
  };

  /*const setPose = (newPose) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    const currentSheetDup = { ...currentSheet };

    currentSheetDup.pose = newPose;
    currentSheetDup.poseId = newPose.id;

    if (sheets[currentSheetIndex]) sheets[currentSheetIndex] = currentSheetDup;

    if (!currentSheet) return;

    sheets[currentSheetIndex].pose = newPose;
    sheets[currentSheetIndex].poseId = newPose.id;

    sheets[currentSheetIndex].pose = newPose;
    sheets[currentSheetIndex].poseId = newPose.id;

    setSheets(() => ([...sheets]));
  };*/

  useEffect(() => {
    console.log('Sheets updated:', sheets);
  }, [sheets]);
  
  // In setPose function
  const setPose = (newPose) => {
    if (sheets.length === 0) return;
  
    setSheets(currentSheets => {
      const updatedSheets = [...currentSheets];
      updatedSheets[currentSheetIndex] = {
        ...updatedSheets[currentSheetIndex],
        pose: newPose,
        poseId: newPose.id
      };
      console.log('Setting new sheets:', updatedSheets);
      return updatedSheets;
    });
  };

  const sheetIds = sheets.map((s) => s.id);

  const sheetsForReviewModal = reviewSheets.map((rs) => {
    if (!sheetIds.includes(rs.id)) return rs;

    return sheets.find((s) => s.id === rs.id);
  });

  if (!sheets[currentSheetIndex]) return null;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }} className={cn({
      siblingImage: sheets[currentSheetIndex]?.pose?.type === 'SiblingImage',
    })}>
      <PreviewArea
        currentSheetIndex={currentSheetIndex}
        sheet={sheets[currentSheetIndex]}
        sheets={sheets}
        setSheets={setSheets}
      />
      <div className={styles.carousels}>
        {nothingToChoose && (
          <p className={styles.jpgPoseSelected}>{NO_USABLE_POSE}</p>
        )}
        {!nothingToChoose && !sheetPoseAllowed && (
          <p className={styles.jpgPoseSelected}>{PICK_ANOTHER_POSE}</p>
        )}
        {!nothingToChoose && (
          <Carousel
            items={posesToDisplay}
            onClick={(pose) => setPose(pose)}
            text="POSE"
            favoriteIds={[]}
          />
        )}
        <div style={{ position: 'relative' }}>
          <div style={{ visibility: initialSheets[currentSheetIndex]?.addon?.backgroundSelection ? 'initial' : 'hidden' }}>
            <Carousel
              items={backgroundsToDisplay}
              onClick={(background) => setBackground(background)}
              text="BACKGROUND"
            />
          </div>
          {!initialSheets[currentSheetIndex]?.addon?.backgroundSelection && (
            <p className={styles.jpgPoseSelected}>The background cannot change on the pose you have selected.</p>
          )}
        </div>
        <div className={styles.btnContainers}>
          <button disabled={decrementDisabled} onClick={decrement} type="button">Back</button>
          {!incrementDisabled && (
            <button disabled={!sheetPoseAllowed} onClick={increment} type="button">
              Confirm
            </button>
          )}
          {incrementDisabled && !sheetPoseAllowed && (
            <button disabled type="button">
              Confirm
            </button>
          )}
          {incrementDisabled && sheetPoseAllowed && (
            <ReviewModal
              additionalPoses={0}
              orderPackage={orderPackage}
              option={option}
              isLoading={isLoading}
              errors={errors}
              packageId={packageId}
              sheets={sheets}
              setIndex={setIndex}
              offer={offer}
              qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
              onConfirm={onConfirm}
              onCancel={onConfirm}
              cancelText="+ Add Another Item"
            />
          )}
        </div>
      </div>
    </div>
  );
}
